import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Trash2, Save, Download, Camera, Upload, 
  ChevronDown, ChevronUp, Image, X, CheckCircle, AlertCircle, Clock
} from 'lucide-react';
import { Pagination, usePagination } from './Pagination';

const API_URL = '';

// Status options and their styling
const STATUS_OPTIONS = ['For rectification', 'In Progress', 'Rectified'];
const STATUS_STYLES = {
  'For rectification': 'bg-red-500/20 text-red-400 border-red-500/30',
  'In Progress': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Rectified': 'bg-green-500/20 text-green-400 border-green-500/30'
};
const STATUS_ICONS = {
  'For rectification': AlertCircle,
  'In Progress': Clock,
  'Rectified': CheckCircle
};

export default function PunchlistTab({ user, projects, isReadOnly = false }) {
  const [punchlists, setPunchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPunchlist, setSelectedPunchlist] = useState(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const fileInputRefs = useRef({});
  const punchlistPage = usePagination(punchlists, 20);

  // Form state
  const [formData, setFormData] = useState({
    project_id: '',
    inspected_by: '',
    inspection_date: '',
    items: []
  });

  const loadPunchlists = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/punchlist`);
      setPunchlists(res.data);
    } catch (err) {
      console.error('Failed to load punchlists:', err);
      toast.error('Failed to load punchlists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPunchlists();
  }, [loadPunchlists]);

  const resetForm = () => {
    setFormData({
      project_id: '',
      inspected_by: user?.full_name || '',
      inspection_date: new Date().toISOString().split('T')[0],
      items: []
    });
    setSelectedPunchlist(null);
    setExpandedItems({});
  };

  const handleNewPunchlist = () => {
    resetForm();
    setShowFormDialog(true);
  };

  const handleEditPunchlist = (punchlist) => {
    setSelectedPunchlist(punchlist);
    setFormData({
      project_id: punchlist.project_id,
      inspected_by: punchlist.inspected_by,
      inspection_date: punchlist.inspection_date,
      items: punchlist.items || []
    });
    // Expand all items by default when editing
    const expanded = {};
    (punchlist.items || []).forEach((_, idx) => {
      expanded[idx] = true;
    });
    setExpandedItems(expanded);
    setShowFormDialog(true);
  };

  const handleViewPunchlist = (punchlist) => {
    setSelectedPunchlist(punchlist);
    setFormData({
      project_id: punchlist.project_id,
      inspected_by: punchlist.inspected_by,
      inspection_date: punchlist.inspection_date,
      items: punchlist.items || []
    });
    setShowDetailDialog(true);
  };

  const addItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      location: '',
      description: '',
      photos: [],
      status: 'For rectification'
    };
    const newItems = [...formData.items, newItem];
    setFormData({ ...formData, items: newItems });
    // Expand the new item
    setExpandedItems({ ...expandedItems, [newItems.length - 1]: true });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const toggleItemExpand = (index) => {
    setExpandedItems({ ...expandedItems, [index]: !expandedItems[index] });
  };

  const handlePhotoUpload = async (index, files) => {
    if (!files || files.length === 0) return;

    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }

    try {
      toast.info('Uploading photos...');
      const res = await axios.post(`${API_URL}/api/punchlist/upload-photos`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newPhotos = res.data.photo_urls || [];
      const currentPhotos = formData.items[index].photos || [];
      updateItem(index, 'photos', [...currentPhotos, ...newPhotos]);
      toast.success(`Uploaded ${newPhotos.length} photo(s)`);
    } catch (err) {
      console.error('Failed to upload photos:', err);
      toast.error('Failed to upload photos');
    }
  };

  const removePhoto = (itemIndex, photoIndex) => {
    const newPhotos = formData.items[itemIndex].photos.filter((_, i) => i !== photoIndex);
    updateItem(itemIndex, 'photos', newPhotos);
  };

  const handleSave = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.inspected_by) {
      toast.error('Please enter inspector name');
      return;
    }
    if (!formData.inspection_date) {
      toast.error('Please select inspection date');
      return;
    }

    setSaving(true);
    try {
      if (selectedPunchlist) {
        await axios.put(`${API_URL}/api/punchlist/${selectedPunchlist.id}`, formData);
        toast.success('Punchlist updated successfully');
      } else {
        await axios.post(`${API_URL}/api/punchlist`, formData);
        toast.success('Punchlist created successfully');
      }
      setShowFormDialog(false);
      loadPunchlists();
    } catch (err) {
      console.error('Failed to save punchlist:', err);
      toast.error(err.response?.data?.detail || 'Failed to save punchlist');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (punchlistId) => {
    if (!window.confirm('Are you sure you want to delete this punchlist?')) return;

    try {
      await axios.delete(`${API_URL}/api/punchlist/${punchlistId}`);
      toast.success('Punchlist deleted');
      loadPunchlists();
    } catch (err) {
      console.error('Failed to delete punchlist:', err);
      toast.error('Failed to delete punchlist');
    }
  };

  const handleStatusUpdate = async (punchlistId, itemId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/punchlist/${punchlistId}/item/${itemId}/status`, {
        status: newStatus
      });
      toast.success('Status updated');
      loadPunchlists();
      // Update local state if in detail view
      if (selectedPunchlist && selectedPunchlist.id === punchlistId) {
        const updatedItems = formData.items.map(item => 
          item.id === itemId ? { ...item, status: newStatus } : item
        );
        setFormData({ ...formData, items: updatedItems });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status');
    }
  };

  const handleExportPdf = async (punchlistId) => {
    try {
      toast.info('Generating PDF… this can take a moment for large punchlists.');

      // 1) Start background generation (no request-time size limit)
      const { data: started } = await axios.post(`${API_URL}/api/punchlist/${punchlistId}/export/pdf/start`);
      const jobId = started.job_id;

      // 2) Poll until ready (every 2s, up to ~5 minutes)
      const maxAttempts = 150;
      const poll = async (attempt = 0) => {
        if (attempt >= maxAttempts) {
          toast.error('PDF is taking too long. Please try again.');
          return;
        }
        let job;
        try {
          const res = await axios.get(`${API_URL}/api/punchlist/export/pdf/job/${jobId}`);
          job = res.data;
        } catch (e) {
          toast.error('Failed to check PDF status. Please try again.');
          return;
        }

        if (job.status === 'done') {
          // 3) Download the finished PDF
          const response = await axios.get(`${API_URL}/api/punchlist/export/pdf/job/${jobId}/download`, {
            responseType: 'blob'
          });
          const ctype = response.headers['content-type'] || response.data?.type || '';
          if (!ctype.includes('application/pdf')) {
            toast.error('Could not generate the PDF. Please try again.');
            return;
          }
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          let filename = job.filename || 'punchlist.pdf';
          const cd = response.headers['content-disposition'];
          if (cd) {
            const m = cd.match(/filename=(.+)/);
            if (m) filename = m[1].replace(/["']/g, '').trim();
          }
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success('PDF downloaded');
        } else if (job.status === 'error') {
          toast.error('Failed to generate PDF. Please try again.');
        } else {
          setTimeout(() => poll(attempt + 1), 2000);
        }
      };
      poll();
    } catch (err) {
      console.error('Failed to export PDF:', err);
      toast.error('Failed to export PDF. Please try again.');
    }
  };

  // Calculate summary stats
  const getStats = (items) => {
    const total = items.length;
    const forRectification = items.filter(i => i.status === 'For rectification').length;
    const inProgress = items.filter(i => i.status === 'In Progress').length;
    const rectified = items.filter(i => i.status === 'Rectified').length;
    return { total, forRectification, inProgress, rectified };
  };

  const renderItemForm = (item, index) => {
    const isExpanded = expandedItems[index];
    const StatusIcon = STATUS_ICONS[item.status];

    return (
      <div key={item.id || index} className="border border-border/50 rounded-lg overflow-hidden bg-card/30">
        {/* Item Header */}
        <div 
          className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => toggleItemExpand(index)}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">Item #{index + 1}</span>
            {item.location && (
              <span className="text-xs text-muted-foreground">- {item.location}</span>
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[item.status]}`}>
              <StatusIcon className="w-3 h-3" />
              {item.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>

        {/* Item Body */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`location-${index}`}>Location</Label>
                <Input
                  id={`location-${index}`}
                  data-testid={`punchlist-item-location-${index}`}
                  value={item.location}
                  onChange={(e) => updateItem(index, 'location', e.target.value)}
                  placeholder="e.g., Kitchen, Bedroom 1, Bathroom"
                  disabled={isReadOnly}
                  className="bg-background/50"
                />
              </div>
              <div>
                <Label htmlFor={`status-${index}`}>Status</Label>
                <select
                  id={`status-${index}`}
                  data-testid={`punchlist-item-status-${index}`}
                  value={item.status}
                  onChange={(e) => updateItem(index, 'status', e.target.value)}
                  disabled={isReadOnly && !['construction', 'admin', 'principal'].includes(user?.role)}
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                >
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor={`description-${index}`}>Description</Label>
              <Textarea
                id={`description-${index}`}
                data-testid={`punchlist-item-description-${index}`}
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                placeholder="Describe the issue or defect..."
                disabled={isReadOnly}
                className="bg-background/50 min-h-[80px]"
              />
            </div>

            {/* Photos Section */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Image className="w-4 h-4" />
                Photos ({item.photos?.length || 0})
              </Label>
              
              {/* Photo Grid */}
              {item.photos && item.photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
                  {item.photos.map((photo, photoIdx) => (
                    <div key={photoIdx} className="relative group aspect-square">
                      <img
                        src={photo}
                        alt={`Item ${index + 1} photo ${photoIdx + 1}`}
                        className="w-full h-full object-cover rounded-md border border-border/50"
                      />
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removePhoto(index, photoIdx)}
                          className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Buttons */}
              {!isReadOnly && (
                <div className="flex gap-2">
                  <input
                    ref={el => fileInputRefs.current[index] = el}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload(index, e.target.files)}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRefs.current[index]?.click()}
                    className="text-xs"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoUpload(index, e.target.files)}
                    className="hidden"
                    id={`camera-${index}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById(`camera-${index}`)?.click()}
                    className="text-xs"
                  >
                    <Camera className="w-3 h-3 mr-1" />
                    Camera
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-effect border-border/40">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-primary" />
              <CardTitle>Punchlist {isReadOnly ? '(Reference)' : ''}</CardTitle>
            </div>
            {!isReadOnly && (
              <Button onClick={handleNewPunchlist} data-testid="btn-new-punchlist">
                <Plus className="w-4 h-4 mr-2" />
                New Punchlist
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : punchlists.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No punchlists found. {!isReadOnly && 'Create one to get started.'}
            </div>
          ) : (
            <>
            <div className="space-y-3">
              {punchlistPage.items.map(punchlist => {
                const stats = getStats(punchlist.items || []);
                return (
                  <div
                    key={punchlist.id}
                    className="p-4 rounded-lg border border-border/50 bg-card/30 hover:bg-card/50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{punchlist.project_name}</h4>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                          <span>Inspected by: {punchlist.inspected_by}</span>
                          <span>Date: {punchlist.inspection_date}</span>
                        </div>
                        <div className="flex gap-3 mt-2">
                          <span className="text-xs px-2 py-0.5 rounded bg-muted/50">{stats.total} items</span>
                          {stats.forRectification > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                              {stats.forRectification} pending
                            </span>
                          )}
                          {stats.inProgress > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-400">
                              {stats.inProgress} in progress
                            </span>
                          )}
                          {stats.rectified > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                              {stats.rectified} done
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPunchlist(punchlist)}
                          data-testid={`btn-view-punchlist-${punchlist.id}`}
                        >
                          View
                        </Button>
                        {!isReadOnly && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPunchlist(punchlist)}
                            data-testid={`btn-edit-punchlist-${punchlist.id}`}
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportPdf(punchlist.id)}
                          data-testid={`btn-export-punchlist-${punchlist.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(punchlist.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`btn-delete-punchlist-${punchlist.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination page={punchlistPage.page} totalPages={punchlistPage.totalPages} setPage={punchlistPage.setPage} total={punchlistPage.total} pageSize={punchlistPage.pageSize} label="punchlists" testIdPrefix="punchlist-pg" />
            </>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog (Create/Edit) */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPunchlist ? 'Edit Punchlist' : 'New Punchlist'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Project Info */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="project_id">Project *</Label>
                <select
                  id="project_id"
                  data-testid="punchlist-project-select"
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="inspected_by">Inspected By *</Label>
                <Input
                  id="inspected_by"
                  data-testid="punchlist-inspected-by"
                  value={formData.inspected_by}
                  onChange={(e) => setFormData({ ...formData, inspected_by: e.target.value })}
                  placeholder="Inspector name"
                  className="bg-background/50"
                  required
                />
              </div>
              <div>
                <Label htmlFor="inspection_date">Date of Inspection *</Label>
                <Input
                  id="inspection_date"
                  data-testid="punchlist-inspection-date"
                  type="date"
                  value={formData.inspection_date}
                  onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
                  className="bg-background/50"
                  required
                />
              </div>
            </div>

            {/* Items Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base">Punchlist Items ({formData.items.length})</Label>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed border-border/50 rounded-lg">
                  No items yet. Click "Add Item" to add punchlist items.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => renderItemForm(item, index))}
                </div>
              )}

              {/* Add Item Button at Bottom */}
              <Button type="button" variant="outline" className="w-full mt-4" onClick={addItem} data-testid="btn-add-item">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
              <Button variant="outline" onClick={() => setShowFormDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="btn-save-punchlist">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Punchlist'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail View Dialog (Read-only with status update) */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Punchlist Details</span>
              {selectedPunchlist && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExportPdf(selectedPunchlist.id)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPunchlist && (
            <div className="space-y-6 py-4">
              {/* Project Info */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Project:</span>
                    <p className="font-medium">{selectedPunchlist.project_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Inspected By:</span>
                    <p className="font-medium">{selectedPunchlist.inspected_by}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date:</span>
                    <p className="font-medium">{selectedPunchlist.inspection_date}</p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              {(() => {
                const stats = getStats(formData.items);
                return (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-muted/20 rounded-lg text-center">
                      <div className="text-2xl font-bold">{stats.total}</div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-400">{stats.forRectification}</div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                    </div>
                    <div className="p-3 bg-orange-500/10 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-400">{stats.inProgress}</div>
                      <div className="text-xs text-muted-foreground">In Progress</div>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-400">{stats.rectified}</div>
                      <div className="text-xs text-muted-foreground">Done</div>
                    </div>
                  </div>
                );
              })()}

              {/* Items List */}
              <div>
                <Label className="text-base mb-3 block">Items</Label>
                {formData.items.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">No items</div>
                ) : (
                  <div className="space-y-3">
                    {formData.items.map((item, index) => {
                      const StatusIcon = STATUS_ICONS[item.status];
                      return (
                        <div key={item.id || index} className="p-4 border border-border/50 rounded-lg bg-card/30">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">#{index + 1}</span>
                                {item.location && (
                                  <span className="text-sm text-muted-foreground">- {item.location}</span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{item.description || 'No description'}</p>
                              
                              {/* Photos */}
                              {item.photos && item.photos.length > 0 && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                  {item.photos.map((photo, photoIdx) => (
                                    <img
                                      key={photoIdx}
                                      src={photo}
                                      alt={`Item ${index + 1} photo ${photoIdx + 1}`}
                                      className="w-16 h-16 object-cover rounded border border-border/50 cursor-pointer hover:opacity-80"
                                      onClick={() => window.open(photo, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Status Update */}
                            <div className="flex items-center gap-2">
                              <select
                                value={item.status}
                                onChange={(e) => handleStatusUpdate(selectedPunchlist.id, item.id, e.target.value)}
                                className={`px-3 py-1.5 rounded-md border text-sm font-medium ${STATUS_STYLES[item.status]}`}
                                data-testid={`status-select-${item.id}`}
                              >
                                {STATUS_OPTIONS.map(status => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t border-border/50">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
